import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { NumericInput } from '../common/NumericInput';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { estimateOccasionalMonthlySavings } from '../../utils/occasionalMonthlyEstimate';
import { formatGuidedFlowTemplate } from '../../utils/guidedFlowUtils';
import { cn } from '../../utils/cn';

const ALLOCATION_VALUES = ['essential', 'lifestyle', 'personal_investment'];
const YEAR_ALL = 'all';

const schema = z.object({
  name: z.string().min(1),
  budget_allocation: z.enum(['essential', 'lifestyle', 'personal_investment']),
  amount: z.string().min(1),
  day_of_month: z.coerce.number().min(1).max(31),
  month: z.coerce.number().min(1).max(12),
  target_year: z.union([z.literal(YEAR_ALL), z.coerce.number().min(2000).max(2100)]),
  create_smoothing_pot: z.boolean().optional(),
});

const MONTHS = [
  { v: 1, l: 'Janeiro' },
  { v: 2, l: 'Fevereiro' },
  { v: 3, l: 'Março' },
  { v: 4, l: 'Abril' },
  { v: 5, l: 'Maio' },
  { v: 6, l: 'Junho' },
  { v: 7, l: 'Julho' },
  { v: 8, l: 'Agosto' },
  { v: 9, l: 'Setembro' },
  { v: 10, l: 'Outubro' },
  { v: 11, l: 'Novembro' },
  { v: 12, l: 'Dezembro' },
];

function buildYearOptions(viewYear) {
  const base = viewYear ?? new Date().getFullYear();
  const years = new Set([base - 1, base, base + 1, base + 2]);
  return Array.from(years)
    .filter((y) => y >= 2000 && y <= 2100)
    .sort((a, b) => a - b);
}

export function AnnualFormDialog({
  isOpen,
  onClose,
  onSubmit,
  initial,
  isPending,
  variant = 'settings',
  viewYear,
}) {
  const isEdit = Boolean(initial?.id);
  const hasPot = Boolean(initial?.pot_id ?? initial?.has_pot);
  const formCopy = variant === 'calendar' ? pt.calendar.form : pt.settings.annual.form;
  const defaultYear = viewYear ?? new Date().getFullYear();
  const yearOptions = useMemo(() => buildYearOptions(defaultYear), [defaultYear]);

  const { register, handleSubmit, control, reset, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      budget_allocation: 'essential',
      amount: '',
      month: new Date().getMonth() + 1,
      day_of_month: 1,
      target_year: defaultYear,
      create_smoothing_pot: false,
    },
  });

  const watchedAmount = useWatch({ control, name: 'amount' });
  const watchedMonth = useWatch({ control, name: 'month' });
  const watchedTargetYear = useWatch({ control, name: 'target_year' });
  const watchedCreatePot = useWatch({ control, name: 'create_smoothing_pot' });

  const resolvedTargetYear =
    watchedTargetYear === YEAR_ALL ? null : Number(watchedTargetYear);

  const savingsEstimate = useMemo(() => {
    if (!watchedCreatePot) return null;
    return estimateOccasionalMonthlySavings(
      watchedAmount,
      watchedMonth,
      new Date(),
      resolvedTargetYear,
    );
  }, [watchedAmount, watchedMonth, watchedCreatePot, resolvedTargetYear]);

  useEffect(() => {
    if (!isOpen) return;
    const allocation = ALLOCATION_VALUES.includes(initial?.budget_allocation)
      ? initial.budget_allocation
      : 'essential';
    const yearValue =
      initial?.target_year != null
        ? initial.target_year
        : (initial?.month != null && !initial?.id ? defaultYear : YEAR_ALL);
    reset({
      name: initial?.name || '',
      budget_allocation: allocation,
      amount: initial?.amount != null ? String(initial.amount).replace('.', ',') : '',
      month: initial?.month ?? new Date().getMonth() + 1,
      day_of_month: initial?.day_of_month ?? 1,
      target_year: yearValue,
      create_smoothing_pot: Boolean(initial?.create_smoothing_pot),
    });
  }, [isOpen, initial, reset, defaultYear]);

  const submit = (values) => {
    const payload = {
      name: values.name,
      budget_allocation: values.budget_allocation,
      amount: values.amount.replace(',', '.'),
      month: values.month,
      day_of_month: values.day_of_month,
      target_year:
        values.target_year === YEAR_ALL ? null : Number(values.target_year),
      is_recurring_yearly: values.target_year === YEAR_ALL,
    };
    if ((!isEdit || !hasPot) && values.create_smoothing_pot) {
      payload.create_smoothing_pot = true;
    }
    onSubmit(payload);
  };

  const selectClassName =
    'h-11 w-full rounded-xl border border-border bg-card px-3 text-sm';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? formCopy.titleEdit : formCopy.titleCreate}
          </DialogTitle>
          <DialogDescription>
            {variant === 'calendar' ? pt.calendar.form.intro : pt.settings.annual.subtitle}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(submit)}>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted-foreground">
              {formCopy.name}
            </label>
            <Input {...register('name')} />
            {formState.errors.name ? (
              <p className="text-sm text-error">{formState.errors.name.message}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-muted-foreground">
              {formCopy.amount}
            </label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <NumericInput
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  placeholder={pt.quickExpense.amountPlaceholder}
                  className="h-11"
                />
              )}
            />
            {formState.errors.amount ? (
              <p className="text-sm text-error">{formState.errors.amount.message}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                {formCopy.month}
              </label>
              <Controller
                name="month"
                control={control}
                render={({ field }) => (
                  <select
                    className={selectClassName}
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  >
                    {MONTHS.map((m) => (
                      <option key={m.v} value={m.v}>{m.l}</option>
                    ))}
                  </select>
                )}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                {formCopy.day}
              </label>
              <Input
                type="number"
                min={1}
                max={31}
                {...register('day_of_month')}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                {formCopy.year}
              </label>
              <Controller
                name="target_year"
                control={control}
                render={({ field }) => (
                  <select
                    className={selectClassName}
                    value={field.value}
                    onChange={(e) => {
                      const raw = e.target.value;
                      field.onChange(raw === YEAR_ALL ? YEAR_ALL : Number(raw));
                    }}
                  >
                    <option value={YEAR_ALL}>{formCopy.yearAll}</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>

          {(!isEdit || !hasPot) ? (
            <Controller
              name="create_smoothing_pot"
              control={control}
              render={({ field }) => (
                <label
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition-colors',
                    field.value
                      ? 'border-sage-400 bg-sage-50/80 dark:border-sage-600 dark:bg-sage-900/50'
                      : 'border-border bg-card hover:border-border dark:hover:border-sage-700',
                  )}
                >
                  <Checkbox
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    className="mt-0.5"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-foreground">
                      {formCopy.createSmoothingPot}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formCopy.createSmoothingPotHint}
                    </span>
                  </span>
                </label>
              )}
            />
          ) : null}

          {isEdit && hasPot ? (
            <p className="rounded-xl border border-sage-700/50 bg-sage-50 dark:border-sage-700 dark:bg-sage-900/50 px-3 py-2 text-xs text-muted-foreground">
              {formCopy.editHasPotHint}
            </p>
          ) : null}

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-muted-foreground">
              {formCopy.allocationLabel}
            </legend>
            <p className="text-xs text-muted-foreground">{formCopy.allocationHelp}</p>
            <Controller
              name="budget_allocation"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  {ALLOCATION_VALUES.map((value) => (
                    <label
                      key={value}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors',
                        field.value === value
                          ? 'border-sage-400 bg-sage-50/80 dark:border-sage-600 dark:bg-sage-900/50'
                          : 'border-border bg-card hover:border-border dark:hover:border-sage-700',
                      )}
                    >
                      <input
                        type="radio"
                        name="budget_allocation"
                        value={value}
                        checked={field.value === value}
                        onChange={() => field.onChange(value)}
                        className="mt-1"
                      />
                      <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-foreground">
                          {formCopy.allocationOptions[value]}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formCopy.allocationHints[value]}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            />
          </fieldset>

          {savingsEstimate ? (
            <div className="rounded-xl border border-sage-700/50 bg-sage-50 dark:border-sage-700 dark:bg-sage-900/60 px-3 py-2.5">
              <p className="text-sm font-medium text-sage-900">
                {formCopy.monthlySimulationTitle}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {formatGuidedFlowTemplate(formCopy.monthlySimulation, {
                  amount: formatEUR(savingsEstimate.monthly),
                  months: savingsEstimate.months,
                })}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {formCopy.potOnSaveHint}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {pt.common.cancel}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? pt.common.loading : formCopy.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
